import { type FC, type ReactElement, type ReactNode } from "react";

interface ContentTab {
  id: string;
  title: string;
  show: boolean;
  render: () => ReactElement;
}

export const ContentHeader: FC<{
  tabs?: ContentTab[];
  selectedTab?: string;
  handleSelectTab?: (tabName: string) => void;
  pageTitle?: string;
  renderCustomTitle?: () => ReactElement;
  hideTitle?: boolean;
  tabsAlign?: "start" | "end";
  tabsActions?: ReactNode;
  /**
   * Additional className to apply to the header. By default, it uses "foc-page_title" for styling, but you can override it by providing your own className.
   */
  className?: string;
}> = ({
  pageTitle,
  renderCustomTitle,
  tabs,
  selectedTab,
  handleSelectTab,
  className,
  hideTitle = false,
  tabsAlign = "end",
  tabsActions,
}) => {
  const visibleTabs = tabs ?? [];
  const activeTab = selectedTab ?? visibleTabs[0]?.id;
  const isCompact = !renderCustomTitle && visibleTabs.length === 0;

  return (
    <header
      className={`${
        className ?? "foc-page_title"
      } foc-content-header ${
        isCompact ? "foc-content-header--compact" : ""
      } ${
        tabsAlign === "start" ? "foc-content-header--tabs-start" : ""
      } !mb-0 !h-auto !whitespace-normal shrink-0 bg-transparent shadow-none`}
    >
      <div className="foc-content-header-layout">
        {!hideTitle && (
          <div className="foc-content-header-title">
            {renderCustomTitle ? (
              renderCustomTitle()
            ) : (
              <span className="foc-clickable text-2xl font-bold text-slate-950">
                {pageTitle}
              </span>
            )}
          </div>
        )}

        {(visibleTabs.length > 0 || tabsActions) && (
          <div className="foc-content-header-tabs flex min-w-0 flex-wrap items-center justify-end gap-3">
            {visibleTabs.length > 0 && (
              <div
                aria-label="Page sections"
                className="foc-content-header-tablist flex min-w-0 flex-wrap items-center gap-1 rounded-xl border border-slate-200 bg-white p-1 shadow-sm"
                role="tablist"
              >
                {visibleTabs.map((tab) => {
                  const isSelected = tab.id === activeTab;

                  return (
                    <button
                      aria-selected={isSelected}
                      className={`cursor-pointer rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
                        isSelected
                          ? "focusoc-theme-surface shadow-sm"
                          : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                      }`}
                      key={tab.id}
                      onClick={() => handleSelectTab?.(tab.id)}
                      role="tab"
                      type="button"
                    >
                      {tab.title}
                    </button>
                  );
                })}
              </div>
            )}

            {tabsActions ? <div className="shrink-0">{tabsActions}</div> : null}
          </div>
        )}
      </div>
    </header>
  );
};
